import { useEffect, useState } from "react";
import axios, { API } from "../api";

/**
 * PhotoShareSheet — drop-down sheet from the camera/review step.
 *
 * Surfaces:
 *   - Share-with-Aura checkbox (sends to /vision/analyze)
 *   - Connected social media targets (X, Snap, TikTok, Spotify-status)
 *   - Include caption text (textarea)
 *   - Email (mailto:)
 *   - Cloud memory (saves to user_photos via /vision/analyze with skip_reaction=true)
 *
 * Props:
 *   imageBase64      — raw base64 (no data: prefix) of the photo to share
 *   defaultCaption?  — pre-fill caption
 *   wallpaperId?     — when set, this is a WALLPAPER share (not a user photo).
 *                       Hides "Share with Aura" option (per user spec) and routes
 *                       X via attachment_kind="wallpaper".
 *   title?           — override the sheet's title text
 *   onClose()        — dismiss
 *   onShareWithAura()— called when user confirms "Share with Aura" checkbox + Share
 *                      (parent does the /vision/analyze call so reaction surfaces in chat)
 *   onComplete?({results}) — fired after all selected targets have been dispatched
 */
export function PhotoShareSheet({ imageBase64, defaultCaption = "", wallpaperId = null, title = "Share this photo", onClose, onShareWithAura, onComplete }) {
  const isWallpaper = !!wallpaperId;
  const [withAura, setWithAura] = useState(!isWallpaper);  // hide/off for wallpaper
  const [withCloud, setWithCloud] = useState(false);
  const [withEmail, setWithEmail] = useState(false);
  const [caption, setCaption] = useState(defaultCaption);
  const [socials, setSocials] = useState({ x: false, snap: false, tiktok: false, instagram: false });
  const [connected, setConnected] = useState({ x: false, snap: false, tiktok: false, spotify: false, instagram: false });
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    // Discover which socials this user has connected so we don't show dead options
    (async () => {
      try {
        const res = await axios.get(`${API}/integrations`);
        const platforms = res.data?.platforms || [];
        const map = {};
        platforms.forEach((p) => { map[p.id] = !!p.connected; });
        setConnected({
          x: !!map.x,
          snap: !!map.snap,
          tiktok: !!map.tiktok,
          spotify: !!map.spotify,
          instagram: !!map.instagram,
        });
      } catch (_) { /* offline / not authed — leave defaults */ }
    })();
  }, []);

  const anySocialPicked = socials.x || socials.snap || socials.tiktok || socials.instagram;
  const canShare = withAura || withCloud || withEmail || anySocialPicked;

  const handleShare = async () => {
    if (!canShare || !imageBase64) return;
    setSending(true);
    setErr("");
    const results = { aura: null, cloud: null, x: null, snap: null, tiktok: null, instagram: null, email: null };

    // First, ensure the photo is persisted on the server so X/Snap/TikTok/IG can attach it.
    // If user picked "Share with Aura", parent handles persistence via /vision/analyze
    // (which already returns a photo_id). Otherwise, we persist it ourselves.
    let photoId = null;
    const needsServerId = socials.x || socials.tiktok || socials.instagram; // all need a public photo_id route
    const needsCloud = withCloud;
    const wantsParentAura = withAura && typeof onShareWithAura === "function";

    try {
      // 1. Share with Aura — parent does it so chat reflows naturally.
      // Parent's onShareWithAura should return the photo_id from /vision/analyze.
      if (wantsParentAura) {
        try {
          const r = await onShareWithAura(caption);
          if (r?.photo_id) photoId = r.photo_id;
          results.aura = "ok";
        } catch (e) { results.aura = "failed"; }
      }

      // 2. If we need a server-side photo_id and don't have one yet, persist now.
      if ((needsServerId || needsCloud) && !photoId) {
        try {
          const res = await axios.post(`${API}/vision/analyze`, {
            image_base64: imageBase64,
            context: caption || null,
            persist_only: true,
          });
          photoId = res.data?.photo_id || null;
          if (needsCloud) results.cloud = "ok";
        } catch (e) {
          if (needsCloud) results.cloud = "failed";
        }
      } else if (needsCloud && photoId) {
        // Photo already persisted via the Aura path
        results.cloud = "ok";
      }

      // 3. X (Twitter) — post tweet w/ image attached (via attachment_id)
      if (socials.x) {
        try {
          await axios.post(`${API}/x/tweet`, {
            text: caption || "",
            attachment_kind: isWallpaper ? "wallpaper" : "user_photo",
            attachment_id: isWallpaper ? wallpaperId : photoId,
          });
          results.x = "ok";
        } catch (e) {
          results.x = "failed";
        }
      }

      // 4. Snap — native share sheet (Web Share API w/ file). TikTok now uses
      //    the real Content Posting API path instead of Web Share.
      if (socials.snap) {
        try {
          const byteString = atob(imageBase64);
          const buf = new Uint8Array(byteString.length);
          for (let i = 0; i < byteString.length; i++) buf[i] = byteString.charCodeAt(i);
          const file = new File([buf], `aura-photo-${Date.now()}.jpg`, { type: "image/jpeg" });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: "Aura",
              text: caption || "",
            });
            results.snap = "ok";
          } else {
            results.snap = "no-share-api";
          }
        } catch (e) {
          if (e?.name !== "AbortError") results.snap = "failed";
        }
      }

      // 4b. TikTok — Content Posting API (Phase 30.N).
      //    Sends the photo into the user's TikTok drafts; they open the
      //    TikTok app to add music + finalize. PULL_FROM_URL on the
      //    server-side public share endpoint (host must be verified in
      //    the TikTok dev portal URL Properties widget).
      if (socials.tiktok) {
        try {
          const payload = isWallpaper
            ? { wallpaper_id: wallpaperId, title: caption || undefined }
            : { photo_id: photoId,        title: caption || undefined };
          await axios.post(`${API}/tiktok/share/photo`, payload);
          results.tiktok = "ok";
        } catch (e) {
          const detail = e?.response?.data?.detail || "";
          // Pass useful TikTok error context up via the err banner
          if (detail) {
            setErr(detail);
          }
          results.tiktok = "failed";
        }
      }

      // 4c. Instagram — Business Login + Content Publishing API (Phase 30.U).
      //    Posts a single image to the user's IG feed via PULL_FROM_URL.
      //    Connected account must be a Business or Creator account.
      if (socials.instagram) {
        try {
          const payload = isWallpaper
            ? { wallpaper_id: wallpaperId, caption: caption || undefined }
            : { photo_id: photoId,         caption: caption || undefined };
          await axios.post(`${API}/instagram/share/photo`, payload);
          results.instagram = "ok";
        } catch (e) {
          const detail = e?.response?.data?.detail || "";
          if (detail) setErr(detail);
          results.instagram = "failed";
        }
      }

      // 5. Email — open default mail client w/ caption (attachment isn't possible via mailto)
      if (withEmail) {
        try {
          const subject = encodeURIComponent("Photo from my Aura");
          const body = encodeURIComponent((caption ? caption + "\n\n" : "") + "(photo attached separately)");
          window.location.href = `mailto:?subject=${subject}&body=${body}`;
          results.email = "ok";
        } catch (e) { results.email = "failed"; }
      }

      // Build a per-platform success/failure summary
      const summary = [];
      if (results.aura === "ok") summary.push("✓ Aura saw it");
      if (results.cloud === "ok") summary.push("✓ Saved to Cloud Memory");
      if (results.x === "ok") summary.push("✓ Posted to X");
      if (results.snap === "ok") summary.push("✓ Snapchat opened");
      if (results.tiktok === "ok") summary.push("✓ Photo sent to TikTok drafts — open the TikTok app to finalize the post");
      if (results.instagram === "ok") summary.push("✓ Posted to Instagram");
      if (results.email === "ok") summary.push("✓ Email drafted");

      const failures = [];
      if (results.x === "failed") failures.push("X");
      if (results.snap === "failed") failures.push("Snap");
      if (results.tiktok === "failed") failures.push("TikTok");
      if (results.instagram === "failed") failures.push("Instagram");
      if (failures.length) summary.push(`✗ Couldn't share to: ${failures.join(", ")}`);

      if (onComplete) onComplete({ results, photo_id: photoId, summary });
      if (summary.length && typeof window !== "undefined") {
        // Surface a brief toast-style alert so user knows what landed where
        // (especially important for TikTok which goes to drafts, not feed)
        try { setTimeout(() => alert(summary.join("\n")), 200); } catch (_) {}
      }
      onClose && onClose();
    } catch (e) {
      setErr("Something didn't go through — try again?");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="photo-share-sheet-overlay" onClick={onClose} data-testid="photo-share-sheet-overlay">
      <div className="photo-share-sheet" onClick={(e) => e.stopPropagation()} data-testid="photo-share-sheet">
        <div className="pss-title">
          <span>{title}</span>
          <button className="pss-close" onClick={onClose} data-testid="pss-close-btn" aria-label="Close">×</button>
        </div>

        {/* Share with Aura — hidden for wallpaper shares per user spec */}
        {!isWallpaper && (
          <div
            className="pss-row"
            onClick={() => setWithAura((v) => !v)}
            data-testid="pss-row-aura"
            role="checkbox"
            aria-checked={withAura}
            tabIndex={0}
          >
            <span className="pss-row-icon">✨</span>
            <div style={{ flex: 1 }}>
              <div className="pss-row-label">Share with Aura</div>
              <div className="pss-row-sub">Let her see this and react</div>
            </div>
            <span className={`pss-checkbox ${withAura ? "is-on" : ""}`} data-testid="pss-checkbox-aura" />
          </div>
        )}

        {/* Connected socials */}
        {(connected.x || connected.snap || connected.tiktok || connected.instagram) && (
          <>
            <div className="pss-section-title">Social</div>
            {connected.x && (
              <div
                className="pss-row"
                onClick={() => setSocials((s) => ({ ...s, x: !s.x }))}
                data-testid="pss-row-x"
              >
                <span className="pss-row-icon">𝕏</span>
                <div className="pss-row-label">Post to X</div>
                <span className={`pss-checkbox ${socials.x ? "is-on" : ""}`} data-testid="pss-checkbox-x" />
              </div>
            )}
            {connected.snap && (
              <div
                className="pss-row"
                onClick={() => setSocials((s) => ({ ...s, snap: !s.snap }))}
                data-testid="pss-row-snap"
              >
                <span className="pss-row-icon">👻</span>
                <div className="pss-row-label">Snapchat</div>
                <span className={`pss-checkbox ${socials.snap ? "is-on" : ""}`} data-testid="pss-checkbox-snap" />
              </div>
            )}
            {connected.tiktok && (
              <div
                className="pss-row"
                onClick={() => setSocials((s) => ({ ...s, tiktok: !s.tiktok }))}
                data-testid="pss-row-tiktok"
              >
                <span className="pss-row-icon">🎵</span>
                <div className="pss-row-label">TikTok</div>
                <span className={`pss-checkbox ${socials.tiktok ? "is-on" : ""}`} data-testid="pss-checkbox-tiktok" />
              </div>
            )}
            {connected.instagram && (
              <div
                className="pss-row"
                onClick={() => setSocials((s) => ({ ...s, instagram: !s.instagram }))}
                data-testid="pss-row-instagram"
              >
                <span className="pss-row-icon">📷</span>
                <div className="pss-row-label">Instagram</div>
                <span className={`pss-checkbox ${socials.instagram ? "is-on" : ""}`} data-testid="pss-checkbox-instagram" />
              </div>
            )}
          </>
        )}

        <div className="pss-divider" />
        <div className="pss-section-title">Other</div>

        <div
          className="pss-row"
          onClick={() => setWithEmail((v) => !v)}
          data-testid="pss-row-email"
        >
          <span className="pss-row-icon">✉️</span>
          <div className="pss-row-label">Email</div>
          <span className={`pss-checkbox ${withEmail ? "is-on" : ""}`} data-testid="pss-checkbox-email" />
        </div>

        {!isWallpaper && (
          <div
            className="pss-row"
            onClick={() => setWithCloud((v) => !v)}
            data-testid="pss-row-cloud"
          >
            <span className="pss-row-icon">☁️</span>
            <div style={{ flex: 1 }}>
              <div className="pss-row-label">Save to Cloud Memory</div>
              <div className="pss-row-sub">Keep it in Aura's vault, no chat</div>
            </div>
            <span className={`pss-checkbox ${withCloud ? "is-on" : ""}`} data-testid="pss-checkbox-cloud" />
          </div>
        )}

        <div className="pss-divider" />
        <div className="pss-section-title">Caption</div>
        <textarea
          rows={2}
          className="camera-caption-input"
          style={{ width: "100%", margin: "0.3rem 0.4rem", resize: "vertical" }}
          placeholder="Add a caption…"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          maxLength={500}
          data-testid="pss-caption-input"
        />

        {err && <div style={{ color: "#f87171", fontSize: "0.85rem", padding: "0.4rem 0.7rem" }} data-testid="pss-error">{err}</div>}

        <div className="pss-actions">
          <button className="pss-cancel-btn" onClick={onClose} disabled={sending} data-testid="pss-cancel-btn">
            Cancel
          </button>
          <button
            className="pss-share-btn"
            onClick={handleShare}
            disabled={sending || !canShare}
            data-testid="pss-share-btn"
          >
            {sending ? "Sharing…" : "Share"}
          </button>
        </div>
      </div>
    </div>
  );
}
